/**
 * Language Store
 *
 * Dedicated Zustand store for managing the user's target learning language.
 * Features:
 * - Persistent storage via localStorage
 * - Backend sync with Supabase users.target_language
 * - Fallback to hardcoded languages if database unavailable
 *
 * @see /docs/plans/multi-language-support-implementation-plan.md - Phase 1
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '@/services/supabase';
import type {
  Language,
  LanguageCode,
  MVPLanguageCode,
} from '@/types/language';
import {
  DEFAULT_LANGUAGE_CODE,
  FALLBACK_LANGUAGES,
  MVP_LANGUAGE_CODES,
  isMVPLanguageCode,
} from '@/types/language';

// ============================================================================
// Types
// ============================================================================

export interface LanguageState {
  /** Currently selected target language for learning */
  targetLanguage: MVPLanguageCode;

  /** Available languages fetched from database */
  availableLanguages: Language[];

  /** Loading state for language operations */
  isLoading: boolean;

  /** Error state for language operations */
  error: string | null;

  /** Whether languages have been fetched from backend */
  isInitialized: boolean;

  // Actions
  setTargetLanguage: (code: MVPLanguageCode) => Promise<void>;
  fetchAvailableLanguages: () => Promise<void>;
  syncWithBackend: () => Promise<void>;
  reset: () => void;
}

// ============================================================================
// Debounce utility
// ============================================================================

let syncTimeoutId: ReturnType<typeof setTimeout> | null = null;
const SYNC_DEBOUNCE_MS = 1000;

/**
 * Debounced sync to prevent rapid backend calls when user switches
 * between languages quickly
 */
function debouncedSync(syncFn: () => Promise<void>) {
  if (syncTimeoutId) {
    clearTimeout(syncTimeoutId);
  }
  syncTimeoutId = setTimeout(() => {
    syncFn().catch((error) => {
      console.warn('Language sync failed:', error);
    });
    syncTimeoutId = null;
  }, SYNC_DEBOUNCE_MS);
}

// ============================================================================
// Store
// ============================================================================

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      targetLanguage: DEFAULT_LANGUAGE_CODE,
      availableLanguages: [...FALLBACK_LANGUAGES],
      isLoading: false,
      error: null,
      isInitialized: false,

      /**
       * Set the target language and sync to backend
       * @param code - MVP language code to set
       */
      setTargetLanguage: async (code: MVPLanguageCode) => {
        // Validate the language code
        if (!isMVPLanguageCode(code)) {
          console.warn(`Invalid language code: ${code}, falling back to ${DEFAULT_LANGUAGE_CODE}`);
          code = DEFAULT_LANGUAGE_CODE;
        }

        const previousLanguage = get().targetLanguage;

        // Update local state immediately for responsive UI
        set({ targetLanguage: code, error: null });

        // Log the change for debugging
        console.log(`🌍 Language changed: ${previousLanguage} → ${code}`);

        // Debounced sync to backend
        debouncedSync(get().syncWithBackend);
      },

      /**
       * Fetch available languages from the database
       * Falls back to hardcoded list if fetch fails
       */
      fetchAvailableLanguages: async () => {
        const { isInitialized } = get();

        // Skip if already initialized (avoid redundant fetches)
        if (isInitialized) {
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const { data, error } = await supabase
            .from('languages')
            .select('*')
            .eq('is_active', true)
            .in('code', MVP_LANGUAGE_CODES as unknown as string[])
            .order('name');

          if (error) {
            throw error;
          }

          if (data && data.length > 0) {
            // Type assertion: database returns Language-compatible objects
            const languages = data as Language[];
            set({
              availableLanguages: languages,
              isLoading: false,
              isInitialized: true,
            });
            console.log(`📚 Loaded ${languages.length} languages from database`);
          } else {
            // No data returned, use fallback
            console.warn('No languages found in database, using fallback');
            set({
              availableLanguages: [...FALLBACK_LANGUAGES],
              isLoading: false,
              isInitialized: true,
            });
          }
        } catch (error) {
          console.error('Failed to fetch languages:', error);
          set({
            error: 'Failed to load languages',
            isLoading: false,
            isInitialized: true,
            // Keep fallback languages on error
            availableLanguages: [...FALLBACK_LANGUAGES],
          });
        }
      },

      /**
       * Sync current language selection to backend (users.target_language)
       * Called with debounce when language changes
       */
      syncWithBackend: async () => {
        const { targetLanguage } = get();

        try {
          // Get current user
          const {
            data: { user },
            error: authError,
          } = await supabase.auth.getUser();

          if (authError || !user) {
            // User not logged in, skip sync (will sync on next login)
            console.log('User not authenticated, skipping language sync');
            return;
          }

          // Update users table with target language
          const { error: updateError } = await supabase
            .from('users')
            .update({ target_language: targetLanguage })
            .eq('user_id', user.id);

          if (updateError) {
            // Log but don't throw - local state is still valid
            console.warn('Failed to sync language to backend:', updateError);
            return;
          }

          console.log(`✅ Language synced to backend: ${targetLanguage}`);
        } catch (error) {
          console.warn('Language sync error:', error);
        }
      },

      /**
       * Reset store to initial state
       */
      reset: () => {
        set({
          targetLanguage: DEFAULT_LANGUAGE_CODE,
          availableLanguages: [...FALLBACK_LANGUAGES],
          isLoading: false,
          error: null,
          isInitialized: false,
        });
      },
    }),
    {
      name: 'converlang-language',
      storage: createJSONStorage(() => localStorage),
      // Only persist the target language - fetch available languages fresh
      partialize: (state) => ({
        targetLanguage: state.targetLanguage,
      }),
      // Handle rehydration
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.warn('Language store rehydration error:', error);
          return;
        }

        if (state) {
          // Validate persisted language code
          if (!isMVPLanguageCode(state.targetLanguage)) {
            console.warn(
              `Invalid persisted language: ${state.targetLanguage}, resetting to default`
            );
            state.targetLanguage = DEFAULT_LANGUAGE_CODE;
          }

          // Mark as not initialized to trigger fresh fetch
          state.isInitialized = false;
          state.availableLanguages = [...FALLBACK_LANGUAGES];

          console.log(`🔄 Language store rehydrated: ${state.targetLanguage}`);
        }
      },
    }
  )
);

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the current language synchronously (for use in services/non-React code)
 */
export function getCurrentLanguage(): MVPLanguageCode {
  return useLanguageStore.getState().targetLanguage;
}

/**
 * Get a language by code from available languages
 */
export function getLanguageByCode(code: LanguageCode): Language | undefined {
  return useLanguageStore.getState().availableLanguages.find((lang) => lang.code === code);
}

/**
 * Initialize the language store - call this early in app initialization
 */
export async function initializeLanguageStore(): Promise<void> {
  const store = useLanguageStore.getState();

  // Fetch available languages from backend
  await store.fetchAvailableLanguages();

  // Try to sync persisted language with backend preference
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (user) {
      // Fetch user's saved target language from database
      const { data: userData, error } = await supabase
        .from('users')
        .select('target_language')
        .eq('user_id', user.id)
        .single();

      if (!error && userData?.target_language) {
        const backendLanguage = userData.target_language as MVPLanguageCode;

        // If backend has a different language, use it (backend is source of truth)
        if (
          isMVPLanguageCode(backendLanguage) &&
          backendLanguage !== store.targetLanguage
        ) {
          console.log(
            `🔄 Syncing language from backend: ${store.targetLanguage} → ${backendLanguage}`
          );
          // Use internal set to avoid triggering sync back to backend
          useLanguageStore.setState({ targetLanguage: backendLanguage });
        }
      }
    }
  } catch (error) {
    console.warn('Failed to sync language from backend:', error);
    // Non-critical, continue with persisted or default language
  }
}

export default useLanguageStore;
